import { MessageInboxProvider } from './MessageInbox';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo, FullCardInfo } from '@lub0v/santa-utils';
import { Stack, Typography, Button, PersonCard, HiddenFace } from '@src/components';
import { BoxSettingsPage } from '@src/pages/BoxPage/full/BoxSettingsPage';
import { CreateCardWizard } from '@src/pages/BoxPage/full/CreateCardWizard';
import { EditCardPage } from '@src/pages/BoxPage/full/EditCardPage';
import { ExchangeHub } from '@src/components';
import { FullBoxFrame } from './FullBoxFrame';
import { BoxMainPage } from './BoxMainPage';
import { MessengerView } from '@src/pages/BoxPage/full/MessengerView';
import { MyCardView } from '@src/pages/BoxPage/full/MyCardView';
import { OrganizerPanel } from './OrganizerPanel';
import { OrganizerHome } from '@src/pages/BoxPage/full/OrganizerHome';
import { ParticipantsGrid } from '@src/pages/BoxPage/full/ParticipantsGrid';
import { WardView } from '@src/pages/BoxPage/full/WardView';
import { ParticipantSelect } from '@src/pages/BoxPage/ParticipantSelect';

import { boxExchangePath, boxPath, boxOrganizerPath, boxWardPath, type BoxRoute } from '@src/routing/routes';

export interface FullBoxPageProps {
  info: BoxInfo;
  cards: CardInfo[];
  card: FullCardInfo | null;
  ward: FullCardInfo | null;
  join: string;
  route: BoxRoute;
  onChanged: () => Promise<void> | void;
  creating: boolean;
  onCreatingChange: (creating: boolean) => void;
}

export function FullBoxPage(props: FullBoxPageProps) {
  return (
    <MessageInboxProvider
      key={props.info.box.key}
      info={props.info}
      cards={props.cards}
      card={props.card}
      ward={props.ward}
    >
      <FullBoxPageContent {...props} />
    </MessageInboxProvider>
  );
}

function FullBoxPageContent({
  info,
  cards,
  card,
  ward,
  join,
  route,
  onChanged,
  creating,
  onCreatingChange,
}: FullBoxPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const participantsRef = useRef<HTMLElement>(null);
  const revealArrival = useRef<(() => void) | null>(null);
  // BrowserRouter commits navigation asynchronously. Resolve only after its
  // destination has mounted, so the view transition never snapshots the hub.
  useLayoutEffect(() => {
    if (route.view === 'exchange') return;
    revealArrival.current?.();
    revealArrival.current = null;
  }, [route.view]);
  useEffect(() => () => revealArrival.current?.(), []);

  function openRevealedCard(fadeIn: boolean) {
    return new Promise<void>((resolve) => {
      revealArrival.current = resolve;
      navigate(boxWardPath(info.box.key), { state: { gifteeRevealArrival: fadeIn } });
    });
  }

  const own = cards.find((item) => item.isYou);
  const canJoinNew =
    (Boolean(info.canCreateCards) || info.isAdmin) &&
    !info.isParticipant &&
    !info.box.isArchived &&
    (Boolean(join) || info.isAdmin);

  useEffect(() => {
    if (creating) {
      return;
    }
    const key = info.box.key;
    // Shared invitations enter through /card. Keep the invitation credential
    // when redirecting an unclaimed visitor to the name picker on box home.
    const home = join ? `${boxPath(key)}?join=${encodeURIComponent(join)}` : boxPath(key);
    if ((route.view === 'settings' || route.view === 'organizer' || route.view === 'organizer_panel' || route.view === 'organizer_legacy') && !info.isAdmin) {
      navigate(home, { replace: true });
      return;
    }
    if (
      ((route.view === 'exchange' && !info.isAdmin) ||
        route.view === 'ward' ||
        route.view === 'card' ||
        route.view === 'card-edit') &&
      !info.isParticipant
    ) {
      navigate(home, { replace: true });
      return;
    }
    if (route.view === 'messenger' && !info.isAdmin && !info.isParticipant) {
      navigate(home, { replace: true });
      return;
    }
    if (route.cardKey && card && route.cardKey !== card.randomKey) {
      navigate(info.isAdmin ? boxPath(key) : boxExchangePath(key));
    }
  }, [card, creating, info, join, route.cardKey, route.view, navigate]);

  useEffect(() => {
    if (location.hash !== '#participants' || route.view !== 'home') return;
    const frame = requestAnimationFrame(() =>
      participantsRef.current?.scrollIntoView({ block: 'start' }),
    );
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.hash, route.view]);

  if (route.view === 'main') {
    return <Navigate to={`${boxPath(info.box.key)}${location.search}${location.hash}`} replace />;
  }

  if (creating) {
    return (
      <CreateCardWizard
        info={info}
        card={card}
        join={join}
        onChanged={onChanged}
        onCancel={() => onCreatingChange(false)}
        onComplete={() => onCreatingChange(false)}
      />
    );
  }

  const joinGame = canJoinNew ? () => onCreatingChange(true) : undefined;
  const showPeople = route.view === 'home';
  const showOrganizerTable = info.isAdmin && route.view === 'organizer_legacy';

  let body = null;
  if (showPeople && (info.isAdmin || info.isParticipant)) {
    body = (
      <BoxMainPage
        info={info}
        cards={cards}
        participantsRef={participantsRef}
        onChanged={onChanged}
      />
    );
  } else if (route.view === 'organizer_panel' && info.isAdmin) {
    body = <Navigate to={`${boxOrganizerPath(info.box.key)}${location.search}${location.hash}`} replace />;
  } else if (route.view === 'organizer' && info.isAdmin) {
    body = <OrganizerPanel info={info} cards={cards} onChanged={onChanged} />;
  } else if (route.view === 'settings' && info.isAdmin) {
    body = <BoxSettingsPage info={info} onChanged={onChanged} showHeader={false} />;
  } else if (route.view === 'exchange' && info.isParticipant) {
    body = (
      <ExchangeHub
        key={`${card?.id ?? 'no-card'}:${info.wardId ?? 'no-giftee'}`}
        info={info}
        card={card}
        ward={ward}
        cards={cards}
        onChanged={onChanged}
        onRevealOpen={openRevealedCard}
      />
    );
  } else if (route.view === 'exchange' && info.isAdmin) {
    body = (
      <Stack gap={6} testId="game-exchange-join">
        <PersonCard
          className="exchange-join-card"
          layout="exchange"
          compact
          edges={1}
          decoration="forest"
          avatar={<HiddenFace size="round" label={t('box.card.hiddenLabel')} />}
          title={t('game.exchange.myCard')}
          name={t('game.exchange.createCard')}
          status={
            <Typography as="p" role="body/sm" color="inherit">
              {t(canJoinNew && info.isActiveDraw ? 'game.exchange.joinAfterDraw' : canJoinNew ? 'game.header.joinBody' : 'game.exchange.joinUnavailable')}
            </Typography>
          }
          actions={
            joinGame ? (
              <Button
                variant={info.isActiveDraw ? 'ghost' : 'primary'}
                size="large"
                onClick={joinGame}
                testId="game-exchange-join-button"
              >
                {t('game.exchange.createCard')}
              </Button>
            ) : undefined
          }
        />
      </Stack>
    );
  } else if (route.view === 'card-edit' && card) {
    body = <EditCardPage info={info} card={card} onChanged={onChanged} />;
  } else if (route.view === 'card' && card) {
    body = <MyCardView info={info} card={card} own={own} onChanged={onChanged} />;
  } else if (route.view === 'ward' && info.isParticipant) {
    body = (
      <WardView
        info={info}
        card={card}
        ward={ward}
        wardSummary={cards.find((item) => item.isYourWard)}
        onChanged={onChanged}
      />
    );
  } else if (route.view === 'messenger' && (info.isAdmin || info.isParticipant)) {
    body = <MessengerView info={info} cards={cards} card={card} ward={ward} />;
  } else if (showOrganizerTable) {
    body = <OrganizerHome info={info} cards={cards} onChanged={onChanged} />;
  } else if (info.isParticipant || info.isAdmin) {
    body = <ParticipantsGrid info={info} cards={cards} />;
  } else {
    body = (
      <ParticipantSelect
        info={info}
        cards={cards}
        join={join}
        isOrganizer={Boolean(info.isAdmin)}
        onJoinNew={joinGame}
      />
    );
  }

  return (
    <FullBoxFrame
      info={info}
      cards={cards}
      hideMobileHeading={route.view === 'organizer' || route.view === 'organizer_panel'}
      route={route.view === 'organizer_panel' || route.view === 'organizer_legacy' ? { ...route, view: 'organizer' } : route}
      onChanged={onChanged}
      participantsRef={participantsRef}
    >
      {body}
    </FullBoxFrame>
  );
}
